/**
 * Notificações (recebimento de fundos, etc.). Persiste em localStorage.
 * Permite marcar como lida e excluir.
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

const STORAGE_KEY = 'pcp_notifications';

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const [list, setList] = useLocalStorage(STORAGE_KEY, []);

  const addNotification = useCallback(
    (payload) => {
      if (!payload) return null;
      const type = payload.type || 'incoming_funds';
      if (type === 'incoming_funds' && !payload.txHash) return null;
      const id = `notif_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const entry = {
        id,
        type,
        txHash: payload.txHash ?? '',
        amount: payload.amount ?? 0,
        symbol: payload.symbol ?? '',
        networkName: payload.networkName ?? '',
        chainId: payload.chainId ?? '',
        createdAt: Date.now(),
        read: false,
        fromAddress: payload.fromAddress ?? '',
        toAddress: payload.toAddress ?? '',
      };
      setList((prev) => {
        if (entry.txHash && prev.some((n) => n.txHash === entry.txHash)) return prev;
        return [entry, ...prev];
      });
      return id;
    },
    [setList]
  );

  const markAsRead = useCallback(
    (id) => {
      setList((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    },
    [setList]
  );

  const markAllAsRead = useCallback(() => {
    setList((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [setList]);

  const removeNotification = useCallback(
    (id) => {
      setList((prev) => prev.filter((n) => n.id !== id));
    },
    [setList]
  );

  const removeAllNotifications = useCallback(() => {
    setList([]);
  }, [setList]);

  const unreadCount = useMemo(() => list.filter((n) => !n.read).length, [list]);

  const value = useMemo(
    () => ({
      notifications: list,
      unreadCount,
      addNotification,
      markAsRead,
      markAllAsRead,
      removeNotification,
      removeAllNotifications,
    }),
    [list, unreadCount, addNotification, markAsRead, markAllAsRead, removeNotification, removeAllNotifications]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  return (
    ctx || {
      notifications: [],
      unreadCount: 0,
      addNotification: () => null,
      markAsRead: () => {},
      markAllAsRead: () => {},
      removeNotification: () => {},
      removeAllNotifications: () => {},
    }
  );
}
